import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { login, error } = useAuth();
    const navigate = useNavigate();

    const [isForgotMode, setIsForgotMode] = useState(false);
    const [forgotMessage, setForgotMessage] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        if (isForgotMode) {
            setSubmitting(true);
            setForgotMessage(null);
            try {
                const { data } = await authAPI.forgotPassword({ email });
                setForgotMessage({ type: 'success', text: data.message });
            } catch (err) {
                const msg = err.response?.data?.message || 'Erreur réseau.';
                setForgotMessage({ type: 'error', text: msg });
            } finally {
                setSubmitting(false);
            }
            return;
        }

        if (!password) return;
        setSubmitting(true);
        try {
            const user = await login(email, password);
            // Redirection selon le rôle
            const redirectMap = { ADMIN: '/admin', PROF: '/prof', PARENT: '/parent' };
            navigate(redirectMap[user.role] || '/login', { replace: true });
        } catch {
            // L'erreur est gérée dans AuthContext
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-light via-white to-green-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl shadow-lg mb-4">
                        <span className="text-white text-2xl font-bold">TC</span>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900">TCVGD</h1>
                    <p className="text-gray-500 mt-1">Tennis Club Veyrier Grand-Donzel</p>
                </div>

                {/* Carte de connexion */}
                <div className="card shadow-xl border-0 overflow-hidden relative">
                    <h2 className="text-center text-gray-800 mb-6">
                        {isForgotMode ? 'Mot de passe oublié' : 'Connexion'}
                    </h2>

                    {error && !isForgotMode && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {forgotMessage && isForgotMode && (
                        <div className={`mb-4 p-3 border rounded-lg text-sm ${
                            forgotMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
                        }`}>
                            {forgotMessage.text}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Adresse email
                            </label>
                            <input
                                id="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                placeholder="votre@email.com"
                                required
                                autoFocus
                            />
                        </div>

                        {!isForgotMode && (
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Mot de passe
                                    </label>
                                    <button 
                                        type="button" 
                                        onClick={() => { setIsForgotMode(true); setForgotMessage(null); }}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        Oublié ?
                                    </button>
                                </div>
                                <div className="relative">
                                    <input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input pr-10"
                                        placeholder="••••••••"
                                        required={!isForgotMode}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            id="login-submit"
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                !isForgotMode && <LogIn className="w-5 h-5" />
                            )}
                            {submitting 
                                ? 'Transmission...' 
                                : (isForgotMode ? 'Envoyer le lien de réinitialisation' : 'Se connecter')
                            }
                        </button>

                        {isForgotMode && (
                            <button
                                type="button"
                                onClick={() => { setIsForgotMode(false); setForgotMessage(null); }}
                                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-2"
                            >
                                ← Retour à la connexion
                            </button>
                        )}
                    </form>

                    {!isForgotMode && (
                        <div className="mt-6 text-center">
                            <p className="text-gray-600 text-sm">
                                Pas encore de compte ?{' '}
                                <Link to="/register" className="text-primary font-medium hover:underline">
                                    S'inscrire
                                </Link>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
